import { Image, type ImageProps } from "@ui/components/Image";
import { tv, type VariantProps } from "@ui/lib/tv";
import { createContext, type ReactNode, useContext } from "react";

const avatar = tv({
  slots: {
    root: "relative inline-flex items-center justify-center",
    fallback: "font-bold leading-none",
    badge: "absolute -bottom-1 -end-1 flex items-center justify-center",
  },
  variants: {
    color: {
      purple: { root: "bg-purple-400", fallback: "text-purple-800" },
      red: { root: "bg-red-300", fallback: "text-red-800" },
      yellow: { root: "bg-yellow-200", fallback: "text-yellow-950" },
      green: { root: "bg-green-200", fallback: "text-green-800" },
      gray: { root: "bg-gray-200", fallback: "text-gray-800" },
    },
    shape: {
      circle: { root: "rounded-full" },
      square: { root: "" },
    },
    size: {
      "20": { root: "size-5", fallback: "text-[8px]" },
      "24": { root: "size-6", fallback: "text-[10px]" },
      "32": { root: "size-8", fallback: "text-xs" },
      "40": { root: "size-10", fallback: "text-base" },
      "44": { root: "size-11", fallback: "text-lg" },
      "52": { root: "size-[52px]", fallback: "text-xl" },
      "74": { root: "size-[74px]", fallback: "text-2xl" },
      "92": { root: "size-[92px]", fallback: "text-3xl" },
      "100": { root: "size-25", fallback: "text-3xl" },
      "140": { root: "size-35", fallback: "text-4xl" },
    },
  },
  compoundVariants: [
    { class: { root: "rounded-md" }, shape: "square", size: ["20", "24"] },
    { class: { root: "rounded-lg" }, shape: "square", size: ["32", "40", "44"] },
    { class: { root: "rounded-xl" }, shape: "square", size: ["52", "74", "92", "100", "140"] },
  ],
  defaultVariants: { color: "yellow", shape: "square" },
});

type AvatarVariants = VariantProps<typeof avatar>;

export type AvatarColor = NonNullable<AvatarVariants["color"]>;
export type AvatarShape = NonNullable<AvatarVariants["shape"]>;
export type AvatarSize = NonNullable<AvatarVariants["size"]>;

interface AvatarContextValue {
  styles: ReturnType<typeof avatar>;
  size: AvatarSize | undefined;
}

const AvatarContext = createContext<AvatarContextValue | null>(null);

interface AvatarRootProps {
  children?: ReactNode;
  className?: string;
  color?: AvatarColor;
  shape?: AvatarShape;
  size?: AvatarSize;
  /** Sugar for the common wiring: renders an `Avatar.Image` when truthy. */
  image?: ImageProps["image"];
  /** Sugar for the common wiring: renders an `Avatar.Fallback` when truthy. */
  initial?: string | null;
}

interface AvatarImageProps {
  image?: ImageProps["image"];
}

interface AvatarFallbackProps {
  children: ReactNode;
}

interface AvatarBadgeProps {
  children: ReactNode;
  className?: string;
}

function AvatarRoot({ children, className, color, shape, size, image, initial }: AvatarRootProps) {
  const styles = avatar({ color, shape, size });
  const contextValue: AvatarContextValue = { styles, size };

  return (
    <AvatarContext value={contextValue}>
      <div className={styles.root({ className })}>
        {image && <AvatarImage image={image} />}
        {initial && <AvatarFallback>{initial}</AvatarFallback>}
        {children}
      </div>
    </AvatarContext>
  );
}

function AvatarImage({ image }: AvatarImageProps) {
  const ctx = useContext(AvatarContext);
  if (!image) return null;

  const sizes = ctx?.size ? `${ctx.size}px` : undefined;

  return (
    <div className="absolute inset-0 overflow-hidden rounded-[inherit]">
      <Image image={image} sizes={sizes} className="absolute inset-0 size-full object-cover" />
    </div>
  );
}

function AvatarFallback({ children }: AvatarFallbackProps) {
  const ctx = useContext(AvatarContext);
  if (!ctx) throw new Error("Avatar.Fallback must be used within Avatar.Root");

  return <span className={ctx.styles.fallback()}>{children}</span>;
}

function AvatarBadge({ children, className }: AvatarBadgeProps) {
  const ctx = useContext(AvatarContext);
  if (!ctx) throw new Error("Avatar.Badge must be used within Avatar.Root");

  return <div className={ctx.styles.badge({ className })}>{children}</div>;
}

export const Avatar = {
  Root: AvatarRoot,
  Image: AvatarImage,
  Fallback: AvatarFallback,
  Badge: AvatarBadge,
};
